import {usePrefersReducedMotion} from '@github-ui/brand-core/hooks/usePrefersReducedMotion'
import {hasWebGLSupport} from '@github-ui/brand-core/utils/platform'
// eslint-disable-next-line import/no-namespace
import * as Octicons from '@primer/octicons-react'
import {Link, Text, useTheme} from '@primer/react'
import {useWindowSize} from '@primer/react-brand'
import {clsx} from 'clsx'
import {AnimatePresence, motion} from 'motion/react'
import {useCallback, useEffect, useMemo, useState} from 'react'

// eslint-disable-next-line import/no-namespace
import * as Data from './../../data'
import type {
  ProductAnnouncementBannerProps as ProductAnnouncementBannerPropsBase,
  ProductAnnouncementBannerVisual,
} from './../ProductAnnouncement/ProductAnnouncement.types'
import {DOM_ANIMATION_IDS, WEBGL_SCENE_IDS} from './ProductAnnouncementBanner.data'
import ProductAnnouncementBannerAnimation from './ProductAnnouncementBannerAnimation/ProductAnnouncementBannerAnimation'

import Styles from './ProductAnnouncementBanner.module.css'

export type ProductAnnouncementBannerProps = Omit<ProductAnnouncementBannerPropsBase, 'announcementId'> & {
  announcementId: keyof typeof Data
}

const defaultProps: Partial<ProductAnnouncementBannerProps> = {
  labelIcon: 'PlusCircleIcon',
  isVisible: true,
}

const ProductAnnouncementBanner = (props: ProductAnnouncementBannerProps) => {
  const initializedProps: ProductAnnouncementBannerProps = {...defaultProps, ...props}
  const {title, label, labelIcon, visual, announcementId, onClick, onDismiss, aria, isVisible, onEvent} =
    initializedProps

  const [hasLoadedVisual, setHasLoadedVisual] = useState<boolean>(false)
  const {resolvedColorScheme: colorScheme} = useTheme()
  const {isMedium: isDesktopView} = useWindowSize()

  const Icon = !labelIcon ? null : typeof labelIcon === 'string' ? Octicons[labelIcon] : labelIcon

  const shouldReduceMotion = usePrefersReducedMotion()
  const shouldUseWebGL = useMemo(
    () => WEBGL_SCENE_IDS.includes(announcementId) && !shouldReduceMotion && hasWebGLSupport(),
    [announcementId, shouldReduceMotion],
  )
  const shouldUseDomAnimation = useMemo(
    () => !shouldUseWebGL && DOM_ANIMATION_IDS.includes(announcementId) && !shouldReduceMotion,
    [announcementId, shouldReduceMotion, shouldUseWebGL],
  )

  const colorMode = useMemo(() => (['dark', 'night'].includes(colorScheme!) ? 'dark' : 'light'), [colorScheme])
  // Use this as array dependency since React doesn't deeply compare objects...
  const visualKey = useMemo(() => JSON.stringify(visual), [visual])

  const getVisualSrc = useCallback(
    (src: ProductAnnouncementBannerVisual['src']) => {
      if (typeof src === 'string') return src
      return colorMode === 'dark' ? src.dark : src.light
    },
    [colorMode],
  )

  const handleOnClick = useCallback(() => {
    onEvent?.('clicked')
    onClick()
  }, [onClick, onEvent])

  useEffect(() => {
    if (!visual) return
    if (!isVisible) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setHasLoadedVisual(false)
      return
    }

    const loadImages = async () => {
      if ('desktop' in visual) {
        const imgDesktop = new Image()
        imgDesktop.src = getVisualSrc(visual.desktop.src)

        const imgMobile = new Image()
        imgMobile.src = getVisualSrc(visual.mobile.src)

        try {
          await Promise.all([imgDesktop.decode(), imgMobile.decode()])
        } catch {
          /* Ignore errors */
        }

        setHasLoadedVisual(true)
      } else {
        const img = new Image()
        img.src = getVisualSrc(visual.src)
        img.onload = () => setHasLoadedVisual(true)
      }
    }

    onEvent?.('displayed')
    void loadImages()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visualKey, isVisible])

  /* TODO: Implement WebGL scene
  useEffect(() => {
    if (shouldUseWebGL && isVisible && !webGLScene.current) {
      setHasLoadedVisual(false)
      webGLScene.current = new BannerScene({
        canvas: canvasRef.current!,
        sceneId: announcementId,
        colorMode,
        onLoad: () => setHasLoadedVisual(true),
      })
    } else if (!isVisible && webGLScene.current) {
      setHasLoadedVisual(false)
      webGLScene.current.destroy()
      webGLScene.current = null
    }
  }, [shouldUseWebGL, announcementId, isVisible])
  */

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          {...(!shouldReduceMotion && {
            initial: {opacity: 0, x: 40},
            animate: {opacity: 1, x: 0},
            exit: {opacity: 0, x: 40},
            transition: {duration: 0.3},
          })}
          className={Styles['ProductAnnouncementBanner']}
          onClick={handleOnClick}
          onKeyDown={event => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              handleOnClick()
            }
          }}
          data-testid="product-announcement-banner"
          tabIndex={0}
        >
          {!shouldUseWebGL && visual && (
            <div
              className={clsx(
                Styles['ProductAnnouncementBanner__visual'],
                !hasLoadedVisual && Styles['ProductAnnouncementBanner__visual--hidden'],
              )}
              {...(visual && {
                style: {
                  ['--offset-x-desktop']: 'desktop' in visual ? visual.desktop.offsetX || '0' : visual.offsetX || '0',
                  ['--offset-y-desktop']: 'desktop' in visual ? visual.desktop.offsetY || '0' : visual.offsetY || '0',
                  ['--offset-x-mobile']: 'desktop' in visual ? visual.mobile.offsetX || '0' : visual.offsetX || '0',
                  ['--offset-y-mobile']: 'desktop' in visual ? visual.mobile.offsetY || '0' : visual.offsetY || '0',
                },
              })}
            >
              {'desktop' in visual ? (
                <picture>
                  <source media="(width < 768px)" srcSet={getVisualSrc(visual.mobile.src)} />
                  <img
                    src={getVisualSrc(visual.desktop.src)}
                    alt={isDesktopView ? visual.desktop.alt : visual.mobile.alt}
                    loading="eager"
                    fetchPriority="high"
                  />
                </picture>
              ) : (
                <img src={getVisualSrc(visual.src)} alt={visual.alt} loading="eager" fetchPriority="high" />
              )}

              {shouldUseDomAnimation && (
                <div className={Styles['ProductAnnouncementBanner__visual-animation']}>
                  <ProductAnnouncementBannerAnimation sceneId={announcementId} />
                </div>
              )}
            </div>
          )}

          {shouldUseWebGL && (
            <div
              className={clsx(
                Styles['ProductAnnouncementBanner__canvas'],
                !hasLoadedVisual && Styles['ProductAnnouncementBanner__canvas--hidden'],
              )}
            >
              {/* <canvas ref={canvasRef} /> */}
            </div>
          )}

          <div className={Styles['ProductAnnouncementBanner__content']}>
            <Text size="large" weight="semibold" className={Styles['ProductAnnouncementBanner__content-title']}>
              {title}
            </Text>
            <Link className={Styles['ProductAnnouncementBanner__content-label']}>
              {Icon && <Icon className={Styles['ProductAnnouncementBanner__content-labelIcon']} />}
              {label}
            </Link>
          </div>

          <div className={Styles['ProductAnnouncementBanner__close']}>
            <button
              aria-label={aria.close}
              onClick={event => {
                event.stopPropagation()
                onEvent?.('dismissed')
                onDismiss()
              }}
            >
              <Octicons.XIcon />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default ProductAnnouncementBanner
